import { action, autorun, computed, makeAutoObservable } from 'mobx';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import {
    FilterMenuItemType,
    FilterStateType,
} from 'src/domains/casino/webview/bonus-engine/campaign-list/filters/filters.type';
import { BonusWinningsModel } from 'src/domains/casino/state/AppCasino/store/campaign-store/BonusWinningsModel';

export class BonusBalanceNavigationState implements FilterStateType {
    public readonly searchInput: FormInputState<string, string>;
    public hideExpired: boolean = false;
    public activeModel: BonusWinningsModel | null = null;

    public selectedCampaignsSet = new Set<string>();
    public selectedRewards: string[] = [];
    public selectedStatuses: string[] = [];

    public constructor(private readonly getBonusModels: () => Array<BonusWinningsModel> | 'loading') {
        this.searchInput = FormInputState.new('');

        autorun(() => {
            if (
                this.searchInput.value.length > 0 ||
                this.selectedCampaignsSet.size > 0 ||
                this.selectedRewards.length > 0 ||
                this.selectedStatuses.length > 0
            ) {
                this.setActiveModel(null);
            }
        });

        makeAutoObservable(this);
    }

    public get isNoFilterSelected(): boolean {
        return (
            this.selectedCampaignsSet.size === 0 &&
            this.selectedRewards.length === 0 &&
            this.selectedStatuses.length === 0
        );
    }

    private filterByExpired = (
        hideExpired: boolean,
        bonusModels: Array<BonusWinningsModel>
    ): Array<BonusWinningsModel> => {
        const output: Array<BonusWinningsModel> = [];

        for (const model of bonusModels) {
            if (hideExpired) {
                if (model.status !== 'expired') {
                    output.push(model);
                }
            } else {
                output.push(model);
            }
        }
        return output;
    };

    @computed public get bonusesForVew(): Array<BonusWinningsModel> | 'loading' {
        const data = this.getBonusModels();

        if (data === 'loading') {
            return 'loading';
        }

        let filteredData = this.filterByExpired(this.hideExpired, data);

        if (this.searchInput.value.length > 0) {
            filteredData = filteredData.filter((model) =>
                model.rewardName?.toLowerCase().includes(this.searchInput.value.toLowerCase())
            );
        }

        if (this.selectedCampaignsSet.size > 0) {
            filteredData = filteredData.filter((model) => {
                const campaign = model.campaignModelWhenReady;
                return campaign !== null && this.selectedCampaignsSet.has(campaign.campaignType);
            });
        }

        if (this.selectedStatuses.length > 0) {
            filteredData = filteredData.filter((model) => {
                return this.selectedStatuses.includes(model.status);
            });
        }

        return filteredData;
    }

    public onToggleExpired = (): void => {
        this.hideExpired = !this.hideExpired;
        this.setActiveModel(null);
    };

    public setActiveModel = (value: BonusWinningsModel | null): void => {
        this.activeModel = value;
    };

    @action public toggleSelectedCampaign = (campaignKey: string): void => {
        if (this.selectedCampaignsSet.has(campaignKey)) {
            this.selectedCampaignsSet.delete(campaignKey);
        } else {
            this.selectedCampaignsSet.add(campaignKey);
        }
    };

    @action public toggleSelectedReward = (rewardKey: string): void => {
        if (this.selectedRewards.includes(rewardKey)) {
            this.selectedRewards = this.selectedRewards.filter((r) => r !== rewardKey);
        } else {
            this.selectedRewards.push(rewardKey);
        }
    };

    @action public toggleSelectedStatus = (statusKey: string): void => {
        if (this.selectedStatuses.includes(statusKey)) {
            this.selectedStatuses = this.selectedStatuses.filter((s) => s !== statusKey);
        } else {
            this.selectedStatuses.push(statusKey);
        }
    };

    @action public clearFilters = (): void => {
        this.selectedCampaignsSet.clear();
        this.selectedRewards = [];
        this.selectedStatuses = [];
    };

    @computed public get filterMenuItems(): FilterMenuItemType[] {
        return [
            {
                label: 'Campaign',
                items: [
                    { key: 'bet-and-get', label: 'Bet and Get' },
                    { key: 'settlement', label: 'Settlement' },
                    { key: 'promo-code', label: 'Promo Code' },
                ],
            },
            {
                label: 'Status',
                items: [
                    { key: 'active', label: 'Active' },
                    { key: 'draft', label: 'Draft' },
                    { key: 'paused', label: 'Paused' },
                    { key: 'expired', label: 'Expired' },
                ],
            },
        ];
    }
}
